// src/Admin/DiplomaManagement.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../Firebase/firebaseConfig';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import ConfirmationModal from '../components/ConfirmationModal/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DiplomaManagement = () => {
  // **Translation Hook**
  const { t, i18n } = useTranslation();

  // **State Variables**
  const [diplomas, setDiplomas] = useState([]);
  const [courses, setCourses] = useState([]);
  const [diplomaForm, setDiplomaForm] = useState({
    name: { en: '', ar: '' },
    description: { en: '', ar: '' },
    duration: { amount: '', unit: 'months' },
    semesters: [
      { courses: [] }, // Semester 1
      { courses: [] }, // Semester 2
      { courses: [] }, // Semester 3
      { courses: [] }, // Semester 4
    ],
  });
  const [editingDiplomaId, setEditingDiplomaId] = useState(null);

  // **State to keep track of selected course per semester**
  const [selectedCourses, setSelectedCourses] = useState(() =>
    Array(4).fill('')
  );

  // **Modal State for Confirmations**
  const [modalData, setModalData] = useState({
    isVisible: false,
    title: '',
    body: '',
    onConfirm: null,
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // **Firestore Collections** (Defined inside useCallback to ensure stability)
  const fetchCourses = useCallback(async () => {
    try {
      const coursesCollection = collection(db, 'courses');
      const querySnapshot = await getDocs(coursesCollection);
      const coursesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCourses(coursesData);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error(t('failedToFetchCourses'));
    }
  }, [t]);

  const fetchDiplomas = useCallback(async () => {
    try {
      const diplomasCollection = collection(db, 'diplomas');
      const querySnapshot = await getDocs(diplomasCollection);
      const diplomasData = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        // Ensure semesters is an array
        const semesters = Array.isArray(data.semesters)
          ? data.semesters.map((semester) => ({
              courses: Array.isArray(semester.courses) ? semester.courses : [],
            }))
          : [
              { courses: [] },
              { courses: [] },
              { courses: [] },
              { courses: [] },
            ];

        return {
          id: doc.id,
          ...data,
          semesters,
        };
      });
      setDiplomas(diplomasData);
    } catch (error) {
      console.error('Error fetching diplomas:', error);
      toast.error(t('failedToFetchDiplomas'));
    }
  }, [t]);

  useEffect(() => {
    fetchCourses();
    fetchDiplomas();
  }, [fetchCourses, fetchDiplomas]);

  // **Handle Diploma Form Changes**
  const handleDiplomaFormChange = (e) => {
    const { name, value } = e.target;
    const [field, lang] = name.split('_'); // e.g., name_en, name_ar

    if (lang) {
      setDiplomaForm((prev) => ({
        ...prev,
        [field]: { 
          ...prev[field], 
          [lang]: value || '' // Ensure it's a string
        },
      }));
    } else if (name.startsWith('duration_')) {
      const durationField = name.split('_')[1]; // amount or unit
      setDiplomaForm((prev) => ({
        ...prev,
        duration: {
          ...prev.duration,
          [durationField]: value || '', // Ensure it's a string
        },
      }));
    } else {
      setDiplomaForm((prev) => ({
        ...prev,
        [name]: value || '', // Ensure it's a string
      }));
    }
  };

  // **Handle Adding a Course to a Semester**
  const handleAddCourseToSemester = (semesterIndex) => {
    const courseId = selectedCourses[semesterIndex];
    if (!courseId) {
      toast.warn(t('selectCourseToAdd'));
      return;
    }

    setDiplomaForm((prev) => {
      const semesters = [...prev.semesters];
      const semesterCourses = semesters[semesterIndex].courses;
      if (!semesterCourses.includes(courseId)) {
        semesterCourses.push(courseId);
        toast.success(t('courseAddedSuccessfully'));
      } else {
        toast.warn(t('courseAlreadyAdded'));
      }
      return { ...prev, semesters };
    });

    // Clear the selected course
    setSelectedCourses((prevSelected) => {
      const newSelected = [...prevSelected];
      newSelected[semesterIndex] = '';
      return newSelected;
    });
  };

  // **Remove Course from Semester**
  const handleRemoveSemesterCourse = (semesterIndex, courseId) => {
    setDiplomaForm((prev) => {
      const semesters = [...prev.semesters];
      semesters[semesterIndex].courses = semesters[semesterIndex].courses.filter(
        (id) => id !== courseId
      );
      return { ...prev, semesters };
    });
    toast.info(t('courseRemovedSuccessfully'));
  };

  // **Validate Form**
  const validateForm = () => {
    const errors = {};

    if (!(diplomaForm.name.en && diplomaForm.name.en.trim())) {
      errors['name.en'] = t('englishNameRequired');
    }

    if (!(diplomaForm.name.ar && diplomaForm.name.ar.trim())) {
      errors['name.ar'] = t('arabicNameRequired');
    }

    if (!(diplomaForm.description.en && diplomaForm.description.en.trim())) {
      errors['description.en'] = t('englishDescriptionRequired');
    }

    if (!(diplomaForm.description.ar && diplomaForm.description.ar.trim())) {
      errors['description.ar'] = t('arabicDescriptionRequired');
    }

    if (!(diplomaForm.duration.amount && diplomaForm.duration.amount.trim())) {
      errors['duration.amount'] = t('durationAmountRequired');
    }

    if (!(diplomaForm.duration.unit && diplomaForm.duration.unit.trim())) {
      errors['duration.unit'] = t('durationUnitRequired');
    }

    // Additional validations can be added here

    return Object.keys(errors).length === 0;
  };

  // **Handle Save Diploma**
  const handleSaveDiploma = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast.error(t('pleaseFixFormErrors'));
      return;
    }

    const diplomaData = {
      ...diplomaForm,
      timestamp: new Date(),
    };

    setIsProcessing(true);

    try {
      if (editingDiplomaId) {
        await updateDoc(doc(db, 'diplomas', editingDiplomaId), diplomaData);
        toast.success(t('diplomaUpdated'));
      } else {
        const newDocRef = doc(collection(db, 'diplomas'));
        await setDoc(newDocRef, diplomaData);
        toast.success(t('diplomaAdded'));
      }
      resetDiplomaForm();
      fetchDiplomas();
    } catch (error) {
      console.error('Error saving diploma:', error);
      toast.error(`${t('failedToSaveDiploma')}: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // **Reset Diploma Form**
  const resetDiplomaForm = () => {
    const initialSemesters = [
      { courses: [] },
      { courses: [] },
      { courses: [] },
      { courses: [] },
    ];
    setDiplomaForm({
      name: { en: '', ar: '' },
      description: { en: '', ar: '' },
      duration: { amount: '', unit: 'months' },
      semesters: initialSemesters,
    });
    setEditingDiplomaId(null);
    setSelectedCourses(initialSemesters.map(() => ''));
    toast.info(t('formResetSuccessfully'));
  };

  // **Edit Diploma**
  const handleEditDiploma = (diploma) => {
    const semesters = Array.isArray(diploma.semesters)
      ? diploma.semesters.map((semester) => ({
          courses: Array.isArray(semester.courses) ? semester.courses : [],
        }))
      : [
          { courses: [] },
          { courses: [] },
          { courses: [] },
          { courses: [] },
        ];
    setDiplomaForm({
      name: {
        en: diploma.name?.en || '',
        ar: diploma.name?.ar || '',
      },
      description: {
        en: diploma.description?.en || '',
        ar: diploma.description?.ar || '',
      },
      duration: {
        amount: diploma.duration?.amount || '',
        unit: diploma.duration?.unit || 'months',
      },
      semesters,
    });
    setEditingDiplomaId(diploma.id);

    // Reset selectedCourses
    setSelectedCourses(semesters.map(() => ''));
  };

  // **Delete Diploma with Modal Confirmation**
  const handleDeleteDiploma = (diplomaId) => {
    setModalData({
      isVisible: true,
      title: t('deleteDiploma'),
      body: t('confirmDeleteDiploma'),
      onConfirm: () => confirmDeleteDiploma(diplomaId),
    });
  };

  const confirmDeleteDiploma = async (diplomaId) => {
    setIsProcessing(true);
    try {
      await deleteDoc(doc(db, 'diplomas', diplomaId));
      toast.success(t('diplomaDeleted'));
      fetchDiplomas();
    } catch (error) {
      console.error('Error deleting diploma:', error);
      toast.error(`${t('failedToDeleteDiploma')}: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setModalData((prev) => ({ ...prev, isVisible: false }));
    }
  };

  // **Handle Modal Cancellation**
  const handleCancelModal = () => {
    setModalData({
      isVisible: false,
      title: '',
      body: '',
      onConfirm: null,
    });
  };

  // **Rendering the Component**
  return (
    <section id="diplomas-section" className="admin-section container my-5">
      <h2 className="mb-4">{t('manageDiplomas')}</h2>
      <form onSubmit={handleSaveDiploma} className="admin-form">
        {/* Diploma Form Fields */}
        <div className="mb-3">
          <label className="form-label">
            {t('name')} ({t('english')}) <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            name="name_en"
            className={`form-control ${!(diplomaForm.name.en && diplomaForm.name.en.trim()) ? 'is-invalid' : ''}`}
            placeholder={t('professionalEnglishNamePlaceholder')}
            value={diplomaForm.name.en}
            onChange={handleDiplomaFormChange}
            required
          />
          {!(diplomaForm.name.en && diplomaForm.name.en.trim()) && (
            <div className="invalid-feedback">{t('englishNameRequired')}</div>
          )}
        </div>
        <div className="mb-3">
          <label className="form-label">
            {t('name')} ({t('arabic')}) <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            name="name_ar"
            className={`form-control ${!(diplomaForm.name.ar && diplomaForm.name.ar.trim()) ? 'is-invalid' : ''}`}
            placeholder={t('professionalArabicNamePlaceholder')}
            value={diplomaForm.name.ar}
            onChange={handleDiplomaFormChange}
            required
          />
          {!(diplomaForm.name.ar && diplomaForm.name.ar.trim()) && (
            <div className="invalid-feedback">{t('arabicNameRequired')}</div>
          )}
        </div>

        {/* Multilingual Description */}
        <div className="mb-3">
          <label className="form-label">
            {t('description')} ({t('english')}) <span className="text-danger">*</span>
          </label>
          <textarea
            name="description_en"
            className={`form-control ${!(diplomaForm.description.en && diplomaForm.description.en.trim()) ? 'is-invalid' : ''}`}
            rows="3"
            placeholder={t('professionalEnglishDescriptionPlaceholder')}
            value={diplomaForm.description.en}
            onChange={handleDiplomaFormChange}
            required
          ></textarea>
          {!(diplomaForm.description.en && diplomaForm.description.en.trim()) && (
            <div className="invalid-feedback">{t('englishDescriptionRequired')}</div>
          )}
        </div>
        <div className="mb-3">
          <label className="form-label">
            {t('description')} ({t('arabic')}) <span className="text-danger">*</span>
          </label>
          <textarea
            name="description_ar"
            className={`form-control ${!(diplomaForm.description.ar && diplomaForm.description.ar.trim()) ? 'is-invalid' : ''}`}
            rows="3"
            placeholder={t('professionalArabicDescriptionPlaceholder')}
            value={diplomaForm.description.ar}
            onChange={handleDiplomaFormChange}
            required
          ></textarea>
          {!(diplomaForm.description.ar && diplomaForm.description.ar.trim()) && (
            <div className="invalid-feedback">{t('arabicDescriptionRequired')}</div>
          )}
        </div>

        {/* Duration Field */}
        <div className="mb-3">
          <label className="form-label">
            {t('duration')} <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <input
              type="number"
              name="duration_amount"
              className={`form-control ${!(diplomaForm.duration.amount && diplomaForm.duration.amount.trim()) ? 'is-invalid' : ''}`}
              placeholder={t('durationAmountPlaceholder')}
              value={diplomaForm.duration.amount}
              onChange={handleDiplomaFormChange}
              min="0"
              required
            />
            <select
              name="duration_unit"
              className={`form-select ${!(diplomaForm.duration.unit && diplomaForm.duration.unit.trim()) ? 'is-invalid' : ''}`}
              value={diplomaForm.duration.unit}
              onChange={handleDiplomaFormChange}
              required
            >
              <option value="days">{t('days')}</option>
              <option value="months">{t('months')}</option>
              <option value="years">{t('years')}</option>
            </select>
            {!(diplomaForm.duration.unit && diplomaForm.duration.unit.trim()) && (
              <div className="invalid-feedback">{t('durationUnitRequired')}</div>
            )}
          </div>
          {!(diplomaForm.duration.amount && diplomaForm.duration.amount.trim()) && (
            <div className="invalid-feedback d-block">
              {t('durationAmountRequired')}
            </div>
          )}
        </div>

        {/* Semesters */}
        {diplomaForm.semesters.map((semester, semesterIndex) => (
          <div key={semesterIndex} className="mb-4">
            <h4 className="mb-3">
              {t('semester')} {semesterIndex + 1}
            </h4>
            <div className="row align-items-center mb-3">
              <div className="col-md-8">
                <select
                  className="form-select"
                  value={selectedCourses[semesterIndex]}
                  onChange={(e) => {
                    const courseId = e.target.value;
                    setSelectedCourses((prevSelected) => {
                      const newSelected = [...prevSelected];
                      newSelected[semesterIndex] = courseId;
                      return newSelected;
                    });
                  }}
                >
                  <option value="">{t('selectCourse')}</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title[i18n.language] || course.title['en']}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <button
                  type="button"
                  className="btn btn-outline-primary w-100"
                  onClick={() => handleAddCourseToSemester(semesterIndex)}
                >
                  {t('addCourse')}
                </button>
              </div>
            </div>
            <ul className="list-group">
              {semester.courses.length > 0 ? (
                semester.courses.map((courseId) => {
                  const course = courses.find((c) => c.id === courseId);
                  return (
                    <li key={courseId} className="list-group-item d-flex justify-content-between align-items-center">
                      {course ? course.title[i18n.language] || course.title['en'] : t('courseNotFound')}
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleRemoveSemesterCourse(semesterIndex, courseId)}
                        aria-label={`${t('remove')} ${t('course')}`}
                      >
                        &times;
                      </button>
                    </li>
                  );
                })
              ) : (
                <li className="list-group-item">{t('noCoursesSelected')}</li>
              )}
            </ul>
          </div>
        ))}

        {/* Form Actions */}
        <div className="d-flex gap-3">
          <button type="submit" className="btn btn-primary" disabled={isProcessing}>
            {editingDiplomaId ? t('updateDiploma') : t('addDiploma')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={resetDiplomaForm}
            disabled={isProcessing}
          >
            {t('resetForm')}
          </button>
        </div>
      </form>

      {/* Diplomas Table */}
      <h2 className="mt-5 mb-4">{t('diplomasList')}</h2>
      <div className="table-responsive">
        <table className="table table-striped table-bordered align-middle">
          <thead className="table-dark">
            <tr>
              <th>{t('name')} ({t('english')})</th>
              <th>{t('name')} ({t('arabic')})</th>
              <th>{t('duration')}</th>
              <th>{t('semesters')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {diplomas.length > 0 ? (
              diplomas.map((diploma) => (
                <tr key={diploma.id}>
                  <td>{diploma.name?.en || t('notAvailable')}</td>
                  <td>{diploma.name?.ar || t('notAvailable')}</td>
                  <td>
                    {diploma.duration.amount
                      ? `${diploma.duration.amount} ${t(diploma.duration.unit)}`
                      : t('notSpecified')}
                  </td>
                  <td>
                    {Array.isArray(diploma.semesters) && diploma.semesters.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {diploma.semesters.map((semester, index) => (
                          <li key={index}>
                            <strong>
                              {t('semester')} {index + 1}:
                            </strong>
                            {semester.courses.length > 0 ? (
                              <ul>
                                {semester.courses.map((courseId) => {
                                  const course = courses.find((c) => c.id === courseId);
                                  return (
                                    <li key={courseId}>
                                      {course ? course.title[i18n.language] || course.title['en'] : t('courseNotFound')}
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <span> {t('noCoursesSelected')}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span>{t('noSemesters')}</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-warning me-2"
                      onClick={() => handleEditDiploma(diploma)}
                    >
                      {t('edit')}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteDiploma(diploma.id)}
                    >
                      {t('delete')}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center">
                  {t('noDiplomasAvailable')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal */}
      {modalData.isVisible && (
        <ConfirmationModal
          title={modalData.title}
          body={modalData.body}
          onConfirm={modalData.onConfirm}
          onCancel={handleCancelModal}
          isProcessing={isProcessing}
        />
      )}

      {/* Toast Container for Notifications */}
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar />
    </section>
  );
};

export default DiplomaManagement;
